import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../context/LanguageContext';
import { useNavigationStack } from '../context/NavigationStackContext';
import { useManageMatterRequestContext } from '../context/ManageMatterRequest';
import { useFetchBusinessAreas } from './useFetchBusinessAreas';
import { IBusinessArea } from '../interface/businessArea';
import { IMatterRequest } from '../interface/matterRequest';
import { EntityStatus } from '../interface/common';

export const useSelectBusinessArea = () => {
  const { t } = useTranslation();
  const { language: appLanguage } = useLanguage();
  const { goBack } = useNavigationStack();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');

  const {
    data: businessAreaList,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchBusinessAreas(
    {
      pageSize: 50,
      search: searchValue,
      extraFilters: { status: EntityStatus.ACTIVE },
    },
    true,
  );

  const refreshBusinessAreaList = () => {
    queryClient.removeQueries({ queryKey: ['business-areas'] });
    return refetch();
  };

  const { ref: paginationRef, inView } = useInView({ threshold: 0.5 });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && businessAreaList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, businessAreaList]);

  const handleSelectBusinessArea = (businessArea: IBusinessArea) => {
    updateManageMatterRequest({
      ...matterRequest,
      businessAreaId: businessArea.id,
      businessArea,
      isBusinessAreaSetByUser: true,
    } as unknown as IMatterRequest);
    goBack();
  };

  return {
    t,
    appLanguage,
    businessAreaList,
    isLoading,
    isFetching,
    searchValue,
    setSearchValue,
    paginationRef,
    hasNextPage,
    refreshBusinessAreaList,
    handleSelectBusinessArea,
    currentValue: matterRequest?.businessAreaId,
  };
};
